import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, Eye, Loader2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { captureHandledException } from '@/lib/sentry';
import {
  CHANGE_IMPACT, formatDateTime, getPolicyVersion, policyKeys, type PolicyVersionSummary,
} from '@/lib/policies';
import { bumpTypeOf, policyContentHash } from '@shared/policy-core';
import { formatPolicyDate, renderPolicyHtml } from '@shared/policy-render';

function HashStatus({ valid }: { valid: boolean }) {
  return valid ? (
    <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="h-4 w-4" /> Matches</span>
  ) : (
    <span className="inline-flex items-center gap-1 text-red-700"><XCircle className="h-4 w-4" /> Mismatch</span>
  );
}

function VersionDialog({ policyId, version, onClose }: { policyId: string; version: string | null; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: policyKeys.version(policyId, version ?? ''),
    queryFn: () => getPolicyVersion(policyId, version as string),
    enabled: Boolean(version),
  });
  const [browserHash, setBrowserHash] = useState<string | null>(null);

  // Recompute the hash here as well, independently of the server's check.
  useEffect(() => {
    setBrowserHash(null);
    if (!data) return;
    let cancelled = false;
    policyContentHash(data.contentMarkdown)
      .then(hash => { if (!cancelled) setBrowserHash(hash); })
      .catch(err => captureHandledException(err, 'policies.versions.hash'));
    return () => { cancelled = true; };
  }, [data]);

  const html = useMemo(() => data && renderPolicyHtml({
    title: data.title,
    version: data.version,
    effectiveDate: data.effectiveDate ?? '',
    contentMarkdown: data.contentMarkdown,
    contentHash: data.contentHash,
  }), [data]);

  return (
    <Dialog open={version !== null} onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Version {version}</DialogTitle>
          <DialogDescription>Exactly as stored. Published versions cannot be changed.</DialogDescription>
        </DialogHeader>
        {isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
        {error && <p className="text-red-600 text-sm">Could not load this version.</p>}
        {data && (
          <div className="space-y-4 text-sm">
            <dl className="grid grid-cols-[11rem_1fr] gap-x-4 gap-y-1.5">
              <dt className="text-slate-500">Stored hash</dt>
              <dd className="font-mono text-xs break-all">{data.contentHash}</dd>
              <dt className="text-slate-500">Content check (server)</dt>
              <dd><HashStatus valid={data.contentHashValid} /></dd>
              <dt className="text-slate-500">Content check (browser)</dt>
              <dd>{browserHash ? <HashStatus valid={browserHash === data.contentHash} /> : <Loader2 className="h-4 w-4 animate-spin" />}</dd>
              <dt className="text-slate-500">Effective</dt>
              <dd>{formatPolicyDate(data.effectiveDate) || '—'}</dd>
              <dt className="text-slate-500">Published</dt>
              <dd>{formatDateTime(data.publishedDate)}{data.publishedBy?.email ? ` by ${data.publishedBy.email}` : ''}</dd>
              {data.summaryOfChanges.length > 0 && (
                <>
                  <dt className="text-slate-500">Summary of changes</dt>
                  <dd><ul className="list-disc pl-5">{data.summaryOfChanges.map(s => <li key={s}>{s}</li>)}</ul></dd>
                </>
              )}
              {data.changelog && (
                <>
                  <dt className="text-slate-500">Internal changelog</dt>
                  <dd>{data.changelog}</dd>
                </>
              )}
            </dl>
            {html && <iframe title={`Version ${data.version}`} srcDoc={html} sandbox="" className="h-[60vh] w-full rounded-md border bg-white" />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PolicyVersions({ policyId, versions }: { policyId: string; versions: PolicyVersionSummary[] }) {
  const [viewing, setViewing] = useState<string | null>(null);

  if (versions.length === 0) {
    return <p className="py-8 text-center text-slate-500">No versions have been published.</p>;
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Version</TableHead>
            <TableHead>Change</TableHead>
            <TableHead>Effective</TableHead>
            <TableHead>Published</TableHead>
            <TableHead>Content hash</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {versions.map((v, i) => {
            const change = v.changeType ?? bumpTypeOf(versions[i + 1]?.version ?? null, v.version);
            return (
              <TableRow key={v.version}>
                <TableCell className="font-medium">{v.version}</TableCell>
                <TableCell>{change ? <Badge variant="outline">{CHANGE_IMPACT[change].label}</Badge> : '—'}</TableCell>
                <TableCell>{formatPolicyDate(v.effectiveDate) || '—'}</TableCell>
                <TableCell>
                  <div>{formatDateTime(v.publishedDate)}</div>
                  {v.publishedBy?.email && <div className="text-xs text-slate-500">{v.publishedBy.email}</div>}
                </TableCell>
                <TableCell>
                  <div className="font-mono text-xs" title={v.contentHash}>{v.contentHash?.slice(0, 12)}…</div>
                  <div className="text-xs"><HashStatus valid={v.contentHashValid} /></div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setViewing(v.version)} title="View">
                      <Eye className="h-4 w-4" />
                    </Button>
                    {v.publicUrl && (
                      <Button variant="outline" size="sm" asChild title="Open public page">
                        <a href={v.publicUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /></a>
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <VersionDialog policyId={policyId} version={viewing} onClose={() => setViewing(null)} />
    </>
  );
}
